import { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { getIn } from 'final-form';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import {
  IKnmErknmFormViolationFix,
  IViolationEliminationFormAttachment,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { getAcceptedViolationFixFileFormats } from 'KnmErknmPackage/ErknmViolationsModule/common/getAcceptedViolationFixFileFormats';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { ViolationFixMaterialTypesEnum } from 'APIPackage/KnmModule/enums/ViolationFixMaterialTypesEnum';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { addNoticeKnmFileSizeErrorBig } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorBig';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { ADD_KNM_DOCUMENT_ERROR } from 'CorePackage/MainModule/constants/notices';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import styles from './ErknmDecisionViolationFixAttachmentsField.module.scss';

interface IErknmDecisionViolationFixAttachmentsFieldProps {
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  fix: IKnmErknmFormViolationFix;
  watchOnly: boolean;
  isDisabledByDirty?: boolean;
  isDisabledByAgreement?: boolean;
  hasUnsignedAct?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  id: string;
}

const MAX_FILE_SIZE = 10 * MB;

export const ErknmDecisionViolationFixAttachmentsField = ({
  fieldName,
  formRef,
  fix,
  watchOnly,
  isDisabledByDirty = false,
  isDisabledByAgreement = false,
  hasUnsignedAct = false,
  isKnm,
  isPm,
  isDecision,
  id,
}: IErknmDecisionViolationFixAttachmentsFieldProps) => {
  const dispatch = useDispatch();

  const { downloadKnmDocumentAttachment, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const attachmentsFieldName = `${fieldName}.attachments`;
  const fileFieldName = `${fieldName}.file`;

  const acceptedFormats = useMemo(() => getAcceptedViolationFixFileFormats(fix).join(','), [fix]);

  const isVideoLinks: boolean = !!fix.type?.some(
    typeItem => typeItem.value?.value === ViolationFixMaterialTypesEnum.VIDEO,
  );
  const hasMaterialWithFiles: boolean = !!fix.type?.some(
    typeItem =>
      typeItem.value?.value === ViolationFixMaterialTypesEnum.DOCUMENT ||
      typeItem.value?.value === ViolationFixMaterialTypesEnum.PHOTO,
  );
  const isOnlyVideoLinks = isVideoLinks && !hasMaterialWithFiles;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const addAttachment = (data: IKnmFormAttachment) => {
    const newViolationsAttachmentItem: IViolationEliminationFormAttachment = {
      attachment: data,
      isNew: true,
      guid: null,
      link: null,
    };

    push && push(attachmentsFieldName, newViolationsAttachmentItem);
  };
  const makeRemoveAttachmentHandler = (index: number) => () => remove && remove(attachmentsFieldName, index);

  const makeFileInputChangeHandler = () => async (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (files) {
      const file = files[0];
      const isSizeValid = file.size <= MAX_FILE_SIZE;

      if (!isSizeValid) {
        dispatch(addNoticeKnmFileSizeErrorBig());
      } else {
        const listId = uuidv4();
        const newAttachment: IKnmFormAttachment = {
          fileName: file.name,
          fileSize: Math.trunc(file.size / 1000) || 0,
          signatureInfo: null,
          isNew: true,
          listId,
          file,
        };

        try {
          const uploadedAttachment = await uploadKnmDocumentAttachment(file);

          addAttachment(newAttachment);

          if (uploadedAttachment) {
            // @ts-ignore
            const violationFixAttachments = formRef?.current.getFieldState(attachmentsFieldName as keyof IKnmForm)
              ?.value as IViolationEliminationFormAttachment[] | undefined;
            const uploadedAttachmentIndex = violationFixAttachments?.findIndex(
              item => item.attachment?.listId === listId,
            );

            if (typeof uploadedAttachmentIndex === 'number' && uploadedAttachmentIndex >= 0) {
              const attachmentFieldName = `${attachmentsFieldName}[${uploadedAttachmentIndex}].attachment` as keyof IKnmForm;
              const newUploadedAttachment: IKnmFormAttachment = {
                ...uploadedAttachment,
                isNew: true,
              };

              // @ts-ignore
              formRef?.current.change(attachmentFieldName, newUploadedAttachment);
            }
          }
        } catch (err) {
          dispatch(
            addNoticeAction({
              renderContent: ADD_KNM_DOCUMENT_ERROR,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.UPLOAD_DOCUMENT,
            }),
          );
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = (downloadingAttachment: IKnmFormAttachment) => () => {
    const { fileName, filePath, fileSize, mimeType } = downloadingAttachment;

    if (filePath && mimeType) {
      downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
    }
  };

  const handleAddLink = useCallback(() => {
    const newViolationsAttachmentItem: IViolationEliminationFormAttachment = {
      attachment: null,
      isNew: true,
      guid: null,
      link: { guid: null, value: null },
    };

    push && push(attachmentsFieldName, newViolationsAttachmentItem);
  }, []);

  const attachmentsValidation = useCallback((value: IViolationEliminationFormAttachment[], values: IKnmForm) => {
    let attachmentsErr;
    const currentFix: IKnmErknmFormViolationFix = getIn(values, fieldName);

    if (!currentFix) return attachmentsErr;

    const hasCurrentMaterialWithFiles: boolean = !!currentFix?.type?.some(
      typeItem =>
        typeItem.value?.value === ViolationFixMaterialTypesEnum.DOCUMENT ||
        typeItem.value?.value === ViolationFixMaterialTypesEnum.PHOTO,
    );

    const acceptedCurrentFormats = getAcceptedViolationFixFileFormats(currentFix).join(',');

    if (hasCurrentMaterialWithFiles && !currentFix?.attachments?.length) {
      attachmentsErr = REQUIRED_ERROR;
    } else if (
      value?.some(
        attachment =>
          !attachment.link &&
          !acceptedCurrentFormats.includes(
            attachment.attachment?.fileName
              ?.toLowerCase()
              .substring(attachment.attachment.fileName.toLowerCase().lastIndexOf('.')) || '',
          ),
      )
    ) {
      attachmentsErr = 'Некорректный формат';
    }

    return attachmentsErr;
  }, []);

  return (
    <>
      <Row>
        <div id="fileAddButton">
          <span className="shared-field-label">
            {getTitleFieldWithRedStar({
              label: 'Файлы',
              field: attachmentsFieldName,
              isPm,
              isKnm,
              isDecision,
            })}
          </span>
          <input
            ref={fileInputRef}
            id={fileFieldName}
            name={fileFieldName}
            type="file"
            accept={acceptedFormats}
            style={{ display: 'none' }}
            onChange={makeFileInputChangeHandler()}
          />
          {(fix.isNew || hasUnsignedAct) &&
            !watchOnly &&
            !isOnlyVideoLinks &&
            !isDisabledByDirty &&
            !isDisabledByAgreement && (
              <label className="shared-link" htmlFor={fileFieldName}>
                Добавить
              </label>
            )}
          {(fix.isNew || hasUnsignedAct) && !watchOnly && isVideoLinks && (
            <Button
              className="shared-btn-add"
              text="Добавить ссылку"
              type="button"
              variant="link"
              color="primary"
              disabled={isDisabledByDirty || isDisabledByAgreement}
              onClick={handleAddLink}
            />
          )}
        </div>
      </Row>

      <FieldArray name={attachmentsFieldName} validate={attachmentsValidation}>
        {({ fields, meta }: FieldArrayRenderProps<IViolationEliminationFormAttachment, HTMLElement>) => {
          return (
            <>
              {!!fields.length && (
                <ul className={styles.AttachmentsList}>
                  {fields.map((attachmentFieldName, attachmentIndex) => {
                    if (fields.value?.[attachmentIndex].link && isVideoLinks) {
                      return (
                        <Row key={attachmentFieldName} notSpace>
                          <TextInputField
                            id={`${id}.attachments[${attachmentIndex}].link.value`}
                            className="shared-row-flex"
                            name={`${attachmentFieldName}.link.value`}
                            placeholder={getTitleFieldWithRedStar({
                              label: 'Введите ссылку',
                              field: `${attachmentFieldName}.link.value`,
                              isPm,
                              isKnm,
                              isDecision,
                            })}
                            disabled={
                              !(fix.isNew || hasUnsignedAct) || watchOnly || isDisabledByDirty || isDisabledByAgreement
                            }
                            validate={makeRequired(REQUIRED_ERROR)}
                          />
                          {(fix.isNew || hasUnsignedAct) &&
                            !watchOnly &&
                            !isDisabledByDirty &&
                            !isDisabledByAgreement && (
                              <CloseButton
                                className="shared-deleting-row-btn"
                                onClick={makeRemoveAttachmentHandler(attachmentIndex)}
                              />
                            )}
                        </Row>
                      );
                    }

                    const attachment = fields.value?.[attachmentIndex].attachment;

                    if (!attachment) {
                      return null;
                    }

                    let progress: number | undefined;

                    if (attachment.isNew) {
                      progress = attachment.dateCreate ? 100 : 0;
                    }

                    return (
                      <li key={`${attachmentFieldName}=${attachmentIndex}`}>
                        <UploadFile
                          fileName={attachment.fileName || ''}
                          fileSize={attachment.fileSize}
                          progress={progress}
                          onFileNameClick={attachment.isNew ? undefined : downloadAttachment(attachment)}
                          onRemove={
                            (fix.isNew || hasUnsignedAct) && !watchOnly
                              ? makeRemoveAttachmentHandler(attachmentIndex)
                              : undefined
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
              {typeof meta.error === 'string' && meta.touched && (
                <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
              )}
            </>
          );
        }}
      </FieldArray>
    </>
  );
};
